// The timeline: rows of cores against virtual time, on canvas.
//
// A row is a RowSpec {id, kind, label, height, ...}; kinds are `chip` (a collapsed
// chip's summary), `tc` (a TensorCore: density when zoomed out, one box per bundle
// when zoomed in, waits hatched over both), `tc.units` / `tc.dma` (a
// TensorCore's sub-rows), `sc` (a SparseCore: dispatches, waits, flag writes, its
// DMAs) and `host` (a chip's firmware commands, placed by anchor.js). The gutter of
// labels is DOM; everything else is two canvases, one for rows and one for hover,
// selection, cursor and arrows, so pointing at things never repaints the rows.

import { el, fmtPs, bytes, showTip, hideTip, lowerIndex, upperIndex, clamp, esc } from "./dom.js";
import { unitRGB, DOMINANT, UNIT_BIT, unitsOf } from "./isa.js";
import { levelFor, sampleColumn, DOM_NONE } from "./bins.js";
import { tickStep, hostLine, hostClass, dmaShort, issuer, coreShort, slotText } from "./fmt.js";

const GUTTER = 170;
export const ROW_H = { chip: 22, tc: 30, "tc.units": 26, "tc.dma": 18, sc: 26, host: 20 };
// An event narrower than this carries no outline and no label anyone can read, so on a
// dense row it is drawn as coverage rather than as itself (see `rowSpan`).
const WIDE = 4;

// The host classes fmt.js sorts commands into, as a state a column can carry.
const HOST_CLASS = ["run", "dma", "sync_flag", "throttle", "status", "other"];

// Where a host event sits on the axis: a firmware command spans its completion, a
// doorbell or an interrupt is a point.
const hostT0 = (e) => (e.span ? e.span[0] : e.ps);
const hostT1 = (e) => (e.span ? e.span[1] : e.ps);
// Where a DMA sits: from its issue (or its start, where the trace has no issue) to
// where it landed. One with neither is parked past the right edge -- it has nowhere to
// be drawn -- and one still open reaches as far as it got.
const dmaT0 = (d) => { const a = Number.isNaN(d.at) ? d.start : d.at; return Number.isNaN(a) ? Infinity : a; };
const dmaT1 = (d) => { const a = dmaT0(d), e = d.open ? (Number.isNaN(d.start) ? a : Math.max(a, d.start)) : d.end; return Number.isNaN(e) ? -Infinity : e; };

function parseColor(c) {
  const m = /^#([0-9a-f]{3,8})$/i.exec(c.trim());
  if (!m) return [136, 136, 136];
  let h = m[1];
  if (h.length === 3) h = h.split("").map((x) => x + x).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export class Timeline {
  constructor(root, ctx, opts = {}) {
    this.ctx = ctx;
    this.model = ctx.model;
    this.links = ctx.links;
    this.bins = ctx.bins;
    this.store = ctx.store;
    this.opts = opts;
    this.rows = [];
    this.t0 = 0; this.t1 = Math.max(1, this.model.tEnd);
    // Axis labels are relative to where the cores got busy; absolute time is in tooltips.
    this.origin = this.model.span ? this.model.span[0] : 0;
    this.dpr = window.devicePixelRatio || 1;
    this.arrows = [];
    this.cursor = NaN;
    this.hl = null;
    this.dirty = 0;
    this.raf = 0;
    this.tmp = {};
    this.rowIx = new Map();
    this.cbuf = [];
    this.build(root);
  }

  // --- DOM -------------------------------------------------------------------------

  build(root) {
    root.classList.add("tl");
    root.style.setProperty("--gutter-w", GUTTER + "px");
    root.tabIndex = 0;
    this.root = root;
    this.axis = el("div", "tl-axis");
    this.corner = el("div", "tl-corner");
    this.axisCanvas = el("canvas");
    this.axis.appendChild(this.corner);
    this.axis.appendChild(this.axisCanvas);
    this.body = el("div", "tl-body");
    this.canvases = el("div", "tl-canvases");
    this.canvas = el("canvas");
    this.overlay = el("canvas", "overlay");
    this.canvases.appendChild(this.canvas);
    this.canvases.appendChild(this.overlay);
    this.gutter = el("div", "tl-gutter");
    this.sizer = el("div", "tl-sizer");
    this.body.appendChild(this.canvases);
    this.body.appendChild(this.gutter);
    this.body.appendChild(this.sizer);
    root.appendChild(this.axis);
    root.appendChild(this.body);
    this.g = this.canvas.getContext("2d");
    this.og = this.overlay.getContext("2d");
    this.ag = this.axisCanvas.getContext("2d");
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this.body);
    this.body.addEventListener("scroll", () => this.invalidate(1));
    this.bind();
    this.resize();
  }

  destroy() {
    this.ro.disconnect();
    cancelAnimationFrame(this.raf);
    hideTip();
  }

  resize() {
    const W = Math.max(10, this.body.clientWidth - GUTTER), H = Math.max(10, this.body.clientHeight);
    this.W = W; this.H = H;
    for (const c of [this.canvas, this.overlay]) {
      c.width = Math.round(W * this.dpr); c.height = Math.round(H * this.dpr);
      c.style.width = W + "px"; c.style.height = H + "px";
    }
    this.canvases.style.width = W + "px";
    this.canvases.style.height = H + "px";
    this.axisCanvas.width = Math.round(W * this.dpr); this.axisCanvas.height = Math.round(22 * this.dpr);
    this.axisCanvas.style.width = W + "px"; this.axisCanvas.style.height = "22px";
    this.invalidate(3);
  }

  setRows(rows) {
    this.rows = rows;
    let y = 0;
    for (const r of rows) { r.y = y; r.h = r.height || ROW_H[r.kind] || 20; y += r.h; }
    this.total = y;
    this.sizer.style.height = y + "px";
    this.gutter.style.height = y + "px";
    this.gutter.textContent = "";
    for (const r of rows) this.gutter.appendChild(this.gutterRow(r));
    this.invalidate(3);
  }

  gutterRow(r) {
    const d = el("div", "tl-row " + r.kind.replace(".", "-") + (r.kind.startsWith("tc.") ? " sub" : "") + " d" + (r.depth ?? (r.kind === "chip" ? 0 : r.kind.startsWith("tc.") ? 2 : 1)));
    d.style.top = r.y + "px";
    d.style.height = r.h + "px";
    d.dataset.row = r.id;
    if (r.toggle) {
      const t = el("span", "tog", r.expanded ? "▾" : "▸");
      d.classList.add("expandable");
      t.title = r.expanded ? "collapse" : "expand";
      t.onclick = (e) => { e.stopPropagation(); r.toggle(); };
      d.appendChild(t);
    }
    if (r.swatch) {
      const s = el("span", "swatch");
      s.style.background = r.swatch;
      d.appendChild(s);
    }
    const l = el("span", "lbl", r.label);
    l.title = r.title || r.label;
    d.appendChild(l);
    if (r.badge) {
      const b = el("span", "badge" + (r.badgeWarn ? " warn" : ""), r.badge);
      if (r.badgeTitle) b.title = r.badgeTitle;
      d.appendChild(b);
    }
    if (r.buttons) {
      const m = el("span", "mini");
      for (const b of r.buttons) {
        const x = el("button", b.on ? "on" : "", b.label);
        x.title = b.title || "";
        x.onclick = (e) => { e.stopPropagation(); b.click(); };
        m.appendChild(x);
      }
      d.appendChild(m);
    }
    d.onclick = () => { if (r.click) r.click(); else if (r.toggle) r.toggle(); };
    d.ondblclick = () => { if (this.opts.onRowDblClick) this.opts.onRowDblClick(r); else this.fitRow(r); };
    if (this.opts.onRowDblClick && (r.kind === "tc" || r.kind === "chip")) l.title = (r.title || r.label) + "\ndouble-click to open the chip page";
    return d;
  }

  // --- view ------------------------------------------------------------------------

  setView(t0, t1, silent) {
    const min = 20;
    if (t1 - t0 < min) t1 = t0 + min;
    const span = t1 - t0;
    const lo = -span * 0.05, hi = this.model.tEnd + span * 0.05;
    if (t0 < lo) { t0 = lo; t1 = t0 + span; }
    if (t1 > hi) { t1 = hi; t0 = t1 - span; }
    if (t0 === this.t0 && t1 === this.t1) return;
    this.t0 = t0; this.t1 = t1;
    this.invalidate(3);
    if (!silent && this.opts.onView) this.opts.onView(t0, t1);
  }
  // The busiest cluster of the cores' activity (the default view); `fitRun` is the whole run, host
  // bring-up and all.
  fitAll() { const [a, b] = this.model.focus; const pad = (b - a) * 0.02; this.setView(a - pad, b + pad); }
  fitRun() { this.setView(0, Math.max(1, this.model.tEnd) * 1.01); }
  fitRow(r) {
    if (r.core) {
      const c = r.core;
      if (c.n) this.setView(Math.max(0, c.tFirst - c.cyclePs), (Number.isNaN(c.tLast) ? this.model.tEnd : c.tLast) + c.cyclePs);
    } else if (r.chip != null) {
      this.setView(0, (this.model.chipTend.get(r.chip) || this.model.tEnd) * 1.02);
    }
  }
  get psPerPx() { return (this.t1 - this.t0) / this.W; }
  x(t) { return (t - this.t0) / this.psPerPx; }
  t(x) { return this.t0 + x * this.psPerPx; }
  zoomAt(px, factor) {
    const t = this.t(px);
    const span = (this.t1 - this.t0) * factor;
    this.setView(t - (t - this.t0) * factor, t - (t - this.t0) * factor + span);
  }
  panPx(dx) {
    const d = dx * this.psPerPx;
    this.setView(this.t0 + d, this.t1 + d);
  }
  zoomTo(t0, t1, pad = 0.15) {
    const span = Math.max(t1 - t0, 50);
    this.setView(t0 - span * pad, t1 + span * pad);
  }
  setCursor(t) { this.cursor = t; this.invalidate(2); }
  highlightRow(id) {
    if (this.hl === id) return;
    this.hl = id;
    for (const d of this.gutter.children) d.classList.toggle("hl", d.dataset.row === id);
    this.invalidate(2);
  }
  setArrows(list) { this.arrows = list || []; this.invalidate(2); }
  scrollToRow(id) {
    const r = this.rows.find((x) => x.id === id);
    if (!r) return;
    const top = this.body.scrollTop, H = this.H;
    if (r.y < top || r.y + r.h > top + H) this.body.scrollTop = Math.max(0, r.y - H / 3);
  }
  rowAtY(y) {
    const yy = y + this.body.scrollTop;
    for (const r of this.rows) if (yy >= r.y && yy < r.y + r.h) return r;
    return null;
  }

  invalidate(bits) {
    this.dirty |= bits;
    if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.frame(); });
  }

  frame() {
    if (this.dirty & 1) { this.paintAxis(); this.paintRows(); }
    if (this.dirty & 3) this.paintOverlay();
    this.dirty = 0;
  }

  // --- painting ----------------------------------------------------------------------

  colors() {
    if (!this.col || this.col.theme !== document.documentElement.dataset.theme) {
      const c = { theme: document.documentElement.dataset.theme, dom: [], u: {} };
      for (const u of DOMINANT) c.dom.push(parseColor(unitRGB(u)));
      for (const k of ["wait", "nop", "idle", "host", "sc", "dma", "sync", "arc", "fg", "dim", "dimmer", "line", "panel", "bg", "accent", "hi", "cross", "sel"]) c.u[k] = unitRGB(k);
      c.waitRGB = parseColor(c.u.wait); c.nopRGB = parseColor(c.u.nop); c.idleRGB = parseColor(c.u.idle);
      c.hostRGB = parseColor(c.u.host); c.scRGB = parseColor(c.u.sc);
      c.hatch = this.makeHatch(c.u.wait);
      this.col = c;
    }
    return this.col;
  }

  makeHatch(color) {
    const p = document.createElement("canvas");
    p.width = 8; p.height = 8;
    const g = p.getContext("2d");
    g.fillStyle = color; g.globalAlpha = 0.35;
    g.fillRect(0, 0, 8, 8);
    g.globalAlpha = 1;
    g.strokeStyle = color; g.lineWidth = 2;
    g.beginPath(); g.moveTo(-2, 10); g.lineTo(10, -2); g.moveTo(-2, 2); g.lineTo(2, -2); g.moveTo(6, 10); g.lineTo(10, 6); g.stroke();
    return this.g.createPattern(p, "repeat");
  }

  paintAxis() {
    const g = this.ag, W = this.W, dpr = this.dpr, col = this.colors();
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, 22);
    const step = tickStep(this.psPerPx);
    g.fillStyle = col.u.dim; g.strokeStyle = col.u.line; g.font = "10px ui-sans-serif, system-ui";
    g.textBaseline = "middle";
    // Labels count from where the cores got busy, unless the window is a sliver far
    // from there, in which case they count from a round time just before the window.
    let o = this.origin;
    if (Math.abs(this.t0 - o) > 200 * (this.t1 - this.t0)) { const big = step * 20; o = Math.floor(this.t0 / big) * big; }
    this.axisOrigin = o;
    for (let t = Math.ceil((this.t0 - o) / step) * step + o; t <= this.t1; t += step) {
      const x = this.x(t);
      g.beginPath(); g.moveTo(x, 14); g.lineTo(x, 22); g.stroke();
      const rel = t - o;
      const unit = Math.abs(rel) >= 1e9 ? 1e9 : Math.abs(rel) >= 1e6 ? 1e6 : Math.abs(rel) >= 1e3 ? 1e3 : 1;
      const digits = clamp(Math.ceil(Math.log10(unit / step)), 0, 6);
      g.fillText(Math.abs(rel) < 1e-9 ? "0" : (rel < 0 ? "−" : "+") + fmtPs(Math.abs(rel), digits), x + 3, 9);
    }
    this.corner.textContent = `${fmtPs(this.t1 - this.t0)} across · 0 = ${fmtPs(o)}`;
    this.corner.title = `axis labels are relative to ${fmtPs(o)} (${o.toLocaleString()} ps), where the cores got busy; absolute times are in the tooltips`;
  }

  paintRows() {
    const g = this.g, W = this.W, H = this.H, dpr = this.dpr, col = this.colors();
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = col.u.bg;
    g.fillRect(0, 0, W, H);
    const top = this.body.scrollTop;
    // Grid lines at the axis ticks.
    const step = tickStep(this.psPerPx);
    g.strokeStyle = col.u.line; g.lineWidth = 1; g.globalAlpha = 0.5;
    g.beginPath();
    for (let t = Math.ceil(this.t0 / step) * step; t <= this.t1; t += step) { const x = Math.round(this.x(t)) + 0.5; g.moveTo(x, 0); g.lineTo(x, H); }
    g.stroke();
    g.globalAlpha = 1;
    for (const r of this.rows) {
      const y = r.y - top;
      if (y + r.h < 0 || y > H) continue;
      g.save();
      g.beginPath(); g.rect(0, y, W, r.h); g.clip();
      const fn = this["paint_" + r.kind.replace(".", "_")];
      if (fn) fn.call(this, g, r, y, r.h);
      g.restore();
      g.strokeStyle = col.u.line; g.globalAlpha = 0.6;
      g.beginPath(); g.moveTo(0, y + r.h - 0.5); g.lineTo(W, y + r.h - 0.5); g.stroke();
      g.globalAlpha = 1;
    }
    // The end of the run.
    const xe = this.x(this.model.tEnd);
    if (xe < W) { g.fillStyle = col.u.panel; g.globalAlpha = 0.5; g.fillRect(xe, 0, W - xe, H); g.globalAlpha = 1; }
  }

  // --- dense rows --------------------------------------------------------------------
  //
  // A row of thousands of events over a thousand pixels paints the same pixels over and
  // over: one SparseCore tile stream has 32,000 waits, and at the whole-run zoom all but
  // a few hundred fall on a column another one already covered. So a row whose events
  // outnumber the columns is drawn by column instead (the trick every profiler plays,
  // https://thume.ca/2021/03/14/iforests/): one pass marks the device-pixel columns each
  // event covers, and one rect comes out per run of columns in the same state. Events
  // wide enough to carry an outline or a label are still drawn as themselves, so nothing
  // that can be read on screen is lost; below that width only coverage is left to see.

  // A row's events, sorted by start, and the slice of them that can be on screen.
  // `ends` is the running maximum of the ends, which is monotone whatever the events
  // do, so one binary search finds the first that can still reach the window and
  // another, on the starts, ends the walk. Both are built once per row and kept: the
  // lists behind them do not change while a trace is open.
  rowSpan(key, items, startOf, endOf) {
    let ix = this.rowIx.get(key);
    if (!ix) {
      let a = items;
      for (let k = 1; k < a.length; k++) if (startOf(a[k]) < startOf(a[k - 1])) { a = Array.from(items).sort((p, q) => startOf(p) - startOf(q)); break; }
      const starts = new Float64Array(a.length), ends = new Float64Array(a.length);
      let mx = -Infinity;
      for (let k = 0; k < a.length; k++) { starts[k] = startOf(a[k]); const e = endOf(a[k]); if (e > mx) mx = e; ends[k] = mx; }
      this.rowIx.set(key, ix = { items: a, starts, ends });
    }
    const lo = upperIndex(ix.ends, this.t0), hi = lowerIndex(ix.starts, this.t1) + 1;
    return { items: ix.items, starts: ix.starts, ends: ix.ends, lo, hi, dense: hi - lo > Math.ceil(this.W * this.dpr) };
  }

  // A column-state buffer, one byte per device pixel (0 is uncovered). A painter takes
  // one per lane it draws and empties them again before the next painter runs.
  cols(k) {
    const n = this.nc = Math.ceil(this.W * this.dpr);
    let a = this.cbuf[k];
    if (!a || a.length < n) a = this.cbuf[k] = new Uint8Array(n);
    else a.fill(0, 0, n);
    return a;
  }

  // The columns of [x0, x1) take `state`: the highest state on a column wins it, or the
  // last one where the painter drew the events over each other anyway (`over`).
  mark(a, x0, x1, state = 1, over = false) {
    let i = Math.floor(x0 * this.dpr), j = Math.ceil(x1 * this.dpr);
    if (j <= i) j = i + 1;              // an event is never less than its own column
    if (i < 0) i = 0;
    if (j > this.nc) j = this.nc;
    for (; i < j; i++) if (over || a[i] < state) a[i] = state;
  }

  // One call per run of columns in the same state, in CSS pixels: fn(x0, x1, state).
  runs(a, fn) {
    const n = this.nc, d = this.dpr;
    for (let i = 0; i < n;) {
      const s = a[i];
      if (!s) { i++; continue; }
      let j = i + 1;
      while (j < n && a[j] === s) j++;
      fn(i / d, j / d, s);
      i = j;
    }
  }

  // Density bars from bins into an ImageData strip, then blitted.
  paintDensity(g, binset, y, h, opts = {}) {
    const W = this.W, col = this.colors(), dpr = this.dpr;
    const pw = Math.round(W * dpr), ph = Math.round(h * dpr);
    if (!this.img || this.img.width !== pw || this.img.height !== ph) this.img = g.createImageData(pw, ph);
    const img = this.img, d = img.data;
    d.fill(0);
    const level = levelFor(binset, this.psPerPx / dpr);
    const out = this.tmp;
    const inner = ph - 2 * dpr;
    // A host wait is drawn as a thin grey floor, not a full-height block: it is the
    // core doing nothing, and it should not compete with the work for the eye.
    const floor = Math.max(dpr, Math.round(inner * (opts.hostFloor ?? 0.18)));
    for (let px = 0; px < pw; px++) {
      const ta = this.t0 + (px / dpr) * this.psPerPx, tb = ta + this.psPerPx / dpr;
      if (tb <= 0 || ta > this.model.tEnd) continue;
      sampleColumn(level, ta, tb, out);
      const busyH = out.busy > 0 ? Math.max(2 * dpr, Math.round(out.busy * inner)) : 0, waitH = Math.round(out.wait * inner), nopH = Math.round(out.nop * inner);
      const hostH = out.hwait > 0.02 ? Math.round(Math.min(out.hwait, 1) * floor) : 0;
      const c = out.dom === DOM_NONE ? col.idleRGB : col.dom[out.dom];
      let yy = ph - dpr;
      const fill = (n, rgb, a) => {
        for (let k = 0; k < n && yy > 0; k++, yy--) {
          const o = (yy * pw + px) * 4;
          d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
        }
      };
      fill(busyH, c, 255);
      fill(waitH, col.waitRGB, 210);
      fill(nopH, col.nopRGB, 255);
      if (busyH + waitH + nopH < floor) { yy = ph - dpr; fill(hostH, col.hostRGB, 160); }
    }
    g.putImageData(img, 0, Math.round(y * dpr), 0, 0, pw, ph);
  }

  paint_chip(g, r, y, h) {
    this.paintDensity(g, this.bins.chip(r.chip), y, h);
    this.paintChipEnd(g, r.chip, y, h);
  }

  paintChipEnd(g, chip, y, h) {
    const col = this.colors();
    const te = this.model.chipTend.get(chip);
    if (te != null && te < this.model.tEnd) {
      const x = this.x(te);
      g.strokeStyle = col.u.dimmer; g.setLineDash([2, 3]);
      g.beginPath(); g.moveTo(x, y); g.lineTo(x, y + h); g.stroke();
      g.setLineDash([]);
    }
  }

  paint_tc(g, r, y, h) {
    const core = r.core, col = this.colors(), cyc = core.cyclePs, m = this.model;
    const fine = this.psPerPx < 2 * cyc;
    // A SparseCore sequencer row carries its tiles in a lower lane, so whether the
    // tiles ran is visible without opening the row.
    let tileLane = null;
    if (core.kind === "sc" && m.tilesOf(core.chip, core.ci).length) {
      const th = Math.round(h * 0.42);
      tileLane = { y: y + h - th, h: th };
      h -= th;
      this.paintTileLane(g, core, tileLane.y, tileLane.h);
    }
    // Dispatch bands.
    const dsp = this.rowSpan("p" + r.id, core.dispatches, (d) => d.t0, (d) => (d.open ? m.tEnd : d.t1));
    let cd = null;
    for (let k = dsp.lo; k < dsp.hi; k++) {
      const d = dsp.items[k];
      const t1 = d.open ? m.tEnd : d.t1;
      if (t1 < this.t0) continue;
      const x0 = this.x(d.t0), x1 = this.x(t1);
      if (dsp.dense && x1 - x0 < WIDE) { this.mark(cd || (cd = this.cols(0)), x0, x1); continue; }
      g.fillStyle = col.u.hi; g.globalAlpha = 0.5;
      g.fillRect(x0, y, x1 - x0, h);
      g.globalAlpha = 1;
      if (!d.open) { g.strokeStyle = col.u.dim; g.beginPath(); g.moveTo(x1 + 0.5, y); g.lineTo(x1 + 0.5, y + h); g.stroke(); }
    }
    if (cd) {
      g.fillStyle = col.u.hi; g.globalAlpha = 0.5;
      this.runs(cd, (a, b) => g.fillRect(a, y, b - a, h));
      g.globalAlpha = 1;
    }
    if (!fine) {
      this.paintDensity(g, this.bins.core(core), y, h);
    } else {
      const i0 = Math.max(0, m.bundleAt(core, this.t0) - 1), i1 = Math.min(core.n - 1, m.bundleAt(core, this.t1) + 1);
      const bh = h - 6, by = y + 3;
      const wide = cyc / this.psPerPx;
      g.font = "10px ui-monospace, monospace"; g.textBaseline = "middle";
      for (let i = i0; i <= i1; i++) {
        const t = core.t[i];
        if (Number.isNaN(t)) continue;
        const x0 = this.x(t), w = Math.max(1, wide - (wide > 3 ? 1 : 0));
        const mask = core.umask[i];
        const units = wide >= 6 ? unitsOf(mask) : null;
        if (units && units.length > 1) {
          const sw = w / units.length;
          for (let k = 0; k < units.length; k++) { g.fillStyle = unitRGB(units[k]); g.fillRect(x0 + k * sw, by, sw, bh); }
        } else {
          const dom = DOMINANT.findIndex((u) => mask & UNIT_BIT[u]);
          g.fillStyle = dom >= 0 ? unitRGB(DOMINANT[dom]) : col.u.dimmer;
          g.fillRect(x0, by, w, bh);
        }
        if (wide >= 34) { g.fillStyle = col.u.bg; g.fillText(String(core.pc[i]), x0 + 3, y + h / 2); }
      }
      // Empty-bundle runs.
      const nb = this.bins.core(core);
      for (let k = 0; k < core.nopN.length; k++) {
        const s = core.nopStep[k], n = core.nopN[k];
        const prev = lowerIndex(core.step, s, core.n), next = prev + 1;
        let start;
        if (next < core.n && !Number.isNaN(core.t[next])) start = core.t[next] - n * cyc;
        else if (prev >= 0) start = core.t[prev] + cyc; else start = 0;
        const end = start + n * cyc;
        if (end < this.t0 || start > this.t1) continue;
        g.fillStyle = col.u.nop;
        g.fillRect(this.x(start), by + bh * 0.35, this.x(end) - this.x(start), bh * 0.3);
      }
      void nb;
    }
    this.paintWaits(g, r.id, y, h);
    this.paintStalls(g, r.id, y, h);
    if (core.kind === "sc") this.paintScDispatches(g, r, y + 1, y + 7);
  }

  // The tiles of one SparseCore as a lane: mean busy fraction as height, coloured by
  // how many of the tiles were active, label with the count when there is room.
  paintTileLane(g, core, y, h) {
    const col = this.colors(), m = this.model;
    const bs = this.bins.tiles(core.chip, core.ci);
    const level = levelFor(bs, this.psPerPx);
    const out = this.tmp;
    g.fillStyle = col.u.panel; g.fillRect(0, y, this.W, h);
    g.strokeStyle = col.u.line; g.beginPath(); g.moveTo(0, y + 0.5); g.lineTo(this.W, y + 0.5); g.stroke();
    let runStart = -1, runActive = 0;
    const W = this.W;
    for (let x = 0; x <= W; x++) {
      let active = 0, busy = 0;
      if (x < W) {
        const ta = this.t(x), tb = this.t(x + 1);
        sampleColumn(level, ta, tb, out);
        active = out.active; busy = out.busy;
        if (active) {
          const hh = Math.max(2, Math.round((h - 3) * Math.max(busy, out.wait * 0.5 + 0.1)));
          g.fillStyle = out.wait > busy ? col.u.wait : col.u.sc;
          g.globalAlpha = 0.35 + 0.65 * (active / bs.nTiles);
          g.fillRect(x, y + h - 1 - hh, 1, hh);
          g.globalAlpha = 1;
        }
      }
      if (active && runStart < 0) { runStart = x; runActive = active; }
      else if (active) runActive = Math.max(runActive, active);
      if (!active && runStart >= 0) {
        if (x - runStart > 36) { g.fillStyle = col.u.fg; g.font = "9px ui-sans-serif, system-ui"; g.textBaseline = "top"; g.fillText(`${runActive}/${bs.nTiles} tiles`, runStart + 3, y + 2); }
        runStart = -1;
      }
    }
    void m;
  }

  // Non-flag stalls (C2C, tile barriers, done spins): hatched like waits, labelled by reason.
  paintStalls(g, rowId, y, h) {
    const ids = this.links.stallsByRow.get(rowId);
    if (!ids || !ids.length) return;
    const m = this.model, col = this.colors();
    const sp = this.rowSpan("s" + rowId, ids, (i) => m.stalls[i].t0, (i) => (m.stalls[i].open ? m.tEnd : m.stalls[i].t1));
    let cs = null;
    g.font = "10px ui-sans-serif, system-ui"; g.textBaseline = "middle";
    for (let k = sp.lo; k < sp.hi; k++) {
      const s = m.stalls[sp.items[k]];
      const t1 = s.open ? m.tEnd : s.t1;
      if (t1 < this.t0) continue;
      const x0 = this.x(s.t0), x1 = Math.max(x0 + 1, this.x(t1));
      if (sp.dense && x1 - x0 < WIDE) { this.mark(cs || (cs = this.cols(0)), x0, x1); continue; }
      g.fillStyle = col.hatch;
      g.fillRect(x0, y + 2, x1 - x0, h - 4);
      g.strokeStyle = col.u.sc; g.strokeRect(x0 + 0.5, y + 2.5, x1 - x0 - 1, h - 5);
      if (x1 - x0 > 50) { g.fillStyle = col.u.fg; g.fillText(`${s.reason}${x1 - x0 > 120 ? "  " + fmtPs(s.stallPs, 1) : ""}`, x0 + 4, y + h / 2); }
    }
    // A run of squashed stalls keeps its outline: that is what tells a stall from a wait.
    if (cs) {
      g.fillStyle = col.hatch; g.strokeStyle = col.u.sc;
      this.runs(cs, (a, b) => { g.fillRect(a, y + 2, b - a, h - 4); g.strokeRect(a + 0.5, y + 2.5, b - a - 1, h - 5); });
    }
  }

  // The host's control_tile dispatches to one SparseCore, as pennants hanging from yTop.
  paintScDispatches(g, r, yTop, yTip) {
    const col = this.colors();
    g.fillStyle = col.u.sc;
    for (const d of this.model.scDispatchesOf(r.chip, r.ci)) {
      if (d.ps < this.t0 || d.ps > this.t1) continue;
      const x = this.x(d.ps);
      g.beginPath(); g.moveTo(x - 4, yTop); g.lineTo(x + 4, yTop); g.lineTo(x, yTip); g.closePath();
      if (d.placed === 0) g.fill(); else { g.strokeStyle = col.u.sc; g.stroke(); }
    }
  }

  // Waits on a row (hatched), from links.waitsByRow.
  paintWaits(g, rowId, y, h) {
    const ids = this.links.waitsByRow.get(rowId);
    if (!ids || !ids.length) return;
    const m = this.model, col = this.colors();
    const sp = this.rowSpan("w" + rowId, ids, (i) => m.waits[i].t0, (i) => (m.waits[i].open ? m.tEnd : m.waits[i].t1));
    const fh = Math.max(3, Math.round(h * 0.28));
    let cw = null, chost = null;
    g.font = "10px ui-sans-serif, system-ui"; g.textBaseline = "middle";
    for (let k = sp.lo; k < sp.hi; k++) {
      const w = m.waits[sp.items[k]];
      const t1 = w.open ? m.tEnd : w.t1;
      if (t1 < this.t0) continue;
      const x0 = this.x(w.t0), x1 = Math.max(x0 + 1, this.x(t1));
      if (sp.dense && x1 - x0 < WIDE) {
        // The two kinds sit in different lanes, so each keeps its own columns.
        if (w.cls === "host") this.mark(chost || (chost = this.cols(1)), x0, x1);
        else this.mark(cw || (cw = this.cols(0)), x0, x1);
        continue;
      }
      if (w.cls === "host") {
        // Idle for the host: a grey floor with an outline, not a block.
        g.fillStyle = col.u.host; g.globalAlpha = 0.25;
        g.fillRect(x0, y + h - 2 - fh, x1 - x0, fh);
        g.globalAlpha = 1;
        g.strokeStyle = col.u.host; g.setLineDash(w.open ? [3, 3] : []);
        g.strokeRect(x0 + 0.5, y + h - 2.5 - fh, Math.max(1, x1 - x0 - 1), fh);
        g.setLineDash([]);
        if (x1 - x0 > 60) { g.fillStyle = col.u.dim; g.fillText(`host · flag ${w.idx}${x1 - x0 > 130 ? "  " + fmtPs(w.stallPs, 1) : ""}`, x0 + 4, y + h - 2 - fh / 2); }
        continue;
      }
      g.fillStyle = col.hatch;
      g.fillRect(x0, y + 2, x1 - x0, h - 4);
      if (w.open) { g.strokeStyle = col.u.wait; g.setLineDash([3, 3]); g.strokeRect(x0 + 0.5, y + 2.5, x1 - x0 - 1, h - 5); g.setLineDash([]); }
      if (x1 - x0 > 40) {
        g.fillStyle = col.u.fg;
        g.fillText(`flag ${w.idx}${w.tile != null ? " t" + w.tile : ""}${x1 - x0 > 110 ? "  " + fmtPs(w.stallPs, 1) : ""}`, x0 + 4, y + h / 2);
      }
    }
    if (cw) { g.fillStyle = col.hatch; this.runs(cw, (a, b) => g.fillRect(a, y + 2, b - a, h - 4)); }
    if (chost) {
      g.fillStyle = col.u.host; g.globalAlpha = 0.25;
      this.runs(chost, (a, b) => g.fillRect(a, y + h - 2 - fh, b - a, fh));
      g.globalAlpha = 1;
    }
  }

  paintTicks(g, key, ids, arr, tOf, y, h, color, label) {
    if (!ids.length) return;
    const sp = this.rowSpan(key, ids, (i) => tOf(arr[i]), (i) => tOf(arr[i]));
    let ct = null;
    g.strokeStyle = color; g.fillStyle = color;
    for (let k = sp.lo; k < sp.hi; k++) {
      const it = arr[sp.items[k]];
      const x = Math.round(this.x(tOf(it))) + 0.5;
      // Ticks a pixel apart are a smear whatever is drawn: keep the columns, not the
      // thousands of strokes.
      if (sp.dense) { this.mark(ct || (ct = this.cols(0)), x - 0.5, x + 0.5); continue; }
      g.beginPath(); g.moveTo(x, y + 2); g.lineTo(x, y + h - 2); g.stroke();
      if (label && this.psPerPx < 100) g.fillText(label(it), x + 2, y + h / 2);
    }
    if (ct) this.runs(ct, (a, b) => g.fillRect(a, y + 2, b - a, h - 4));
  }

  paint_tc_units(g, r, y, h) {
    const core = r.core, m = this.model;
    const lanes = ["mxu", "xlu", "eup", "prng"], lh = h / lanes.length;
    const bi0 = Math.max(0, m.bundleAt(core, this.t0)), bi1 = m.bundleAt(core, this.t1);
    const k0 = upperIndex(core.unitBi, bi0);
    g.font = "9px ui-monospace, monospace"; g.textBaseline = "middle";
    const wide = core.cyclePs / this.psPerPx;
    for (let k = k0; k < core.units.length; k++) {
      const u = core.units[k], bi = core.unitBi[k];
      if (bi < 0) continue;
      if (bi > bi1) break;
      const t = core.t[bi], lane = lanes.indexOf(u.unit);
      if (lane < 0) continue;
      const x = this.x(t), ly = y + lane * lh;
      const c = unitRGB(u.unit);
      if (u.op === "pop") { g.strokeStyle = c; g.strokeRect(x + 0.5, ly + 1.5, Math.max(2, wide - 1), lh - 3); }
      else { g.fillStyle = c; g.fillRect(x, ly + 1, Math.max(2, wide - 1), lh - 2); }
      if (wide > 40) { g.fillStyle = c; g.fillText(u.op, x + wide + 2, ly + lh / 2); }
    }
    g.fillStyle = this.colors().u.dimmer; g.font = "9px ui-sans-serif, system-ui";
    for (let i = 0; i < lanes.length; i++) g.fillText(lanes[i], 2, y + i * lh + lh / 2);
  }

  paintDmas(g, rowKey, y, h) {
    const ids = this.links.dmaByIssuer.get(rowKey);
    if (!ids || !ids.length) return;
    const m = this.model, col = this.colors();
    const sp = this.rowSpan("d" + rowKey, ids, (i) => dmaT0(m.dmas[i]), (i) => dmaT1(m.dmas[i]));
    let cq = null, cb = null;          // the issue → start bar, and the block
    g.font = "10px ui-sans-serif, system-ui"; g.textBaseline = "middle";
    for (let k = sp.lo; k < sp.hi; k++) {
      const d = m.dmas[sp.items[k]];
      const a = dmaT0(d), e = dmaT1(d);
      if (e < this.t0) continue;
      const xa = this.x(a), xs = Number.isNaN(d.start) ? xa : this.x(d.start), xe = Math.max(xa + 1, this.x(e));
      if (sp.dense && xe - xs < WIDE) {
        if (xs > xa) this.mark(cq || (cq = this.cols(0)), xa, xs);
        this.mark(cb || (cb = this.cols(1)), xs, xe, d.open ? 3 : d.cross ? 2 : 1);
        continue;
      }
      g.fillStyle = col.u.dma; g.globalAlpha = 0.35;
      g.fillRect(xa, y + h / 2 - 2, Math.max(1, xs - xa), 4);
      g.globalAlpha = 1;
      g.fillRect(xs, y + 3, Math.max(1, xe - xs), h - 6);
      if (d.cross) { g.strokeStyle = col.u.cross; g.strokeRect(xs + 0.5, y + 3.5, Math.max(1, xe - xs) - 1, h - 7); }
      if (d.open) { g.strokeStyle = col.u.dma; g.setLineDash([2, 2]); g.strokeRect(xs + 0.5, y + 3.5, Math.max(6, xe - xs), h - 7); g.setLineDash([]); }
      if (xe - xs > 50) { g.fillStyle = col.u.bg; g.fillText(dmaShort(d), xs + 3, y + h / 2); }
    }
    if (cq) {
      g.fillStyle = col.u.dma; g.globalAlpha = 0.35;
      this.runs(cq, (a, b) => g.fillRect(a, y + h / 2 - 2, b - a, 4));
      g.globalAlpha = 1;
    }
    // Cross-chip and still-open runs keep their outline; the rest are plain blocks.
    if (cb) this.runs(cb, (a, b, s) => {
      g.fillStyle = col.u.dma;
      g.fillRect(a, y + 3, b - a, h - 6);
      if (s === 1) return;
      g.strokeStyle = s === 3 ? col.u.dma : col.u.cross;
      if (s === 3) g.setLineDash([2, 2]);
      g.strokeRect(a + 0.5, y + 3.5, b - a - 1, h - 7);
      g.setLineDash([]);
    });
  }

  paint_tc_dma(g, r, y, h) { this.paintDmas(g, r.core.key, y, h); }

  paint_sc(g, r, y, h) {
    const m = this.model, col = this.colors();
    const half = h / 2;
    this.paintScDispatches(g, r, y + 2, y + 9);
    this.paintWaits(g, r.id, y, h);
    const ids = this.links.flagsByRow.get(r.id) || [];
    g.font = "9px ui-monospace, monospace"; g.textBaseline = "middle";
    this.paintTicks(g, "f" + r.id, ids, m.flags, (f) => f.at, y + half, half, col.u.sync, (f) => `${f.idx}${f.op === "incr" ? (f.delta >= 0 ? "+" : "") + f.delta : "=" + f.new}`);
    this.paintDmas(g, r.id, y + half, half);
  }

  hostColorOf(cls) {
    const col = this.colors();
    return { run: col.u.accent, dma: col.u.dma, sync_flag: col.u.sync, throttle: col.u.dim, status: col.u.dimmer, other: col.u.dimmer }[cls];
  }
  hostColor(kind) { return this.hostColorOf(hostClass(kind)); }

  // A chip's host events, sorted by where anchor.js placed them (wall-clock order is
  // nearly that order, but an exactly placed command can step out of it).
  hostSpan(chip) {
    return this.rowSpan("h" + chip, this.model.hostByChip.get(chip) || [], hostT0, hostT1);
  }

  paint_host(g, r, y, h) {
    const col = this.colors();
    const sp = this.hostSpan(r.chip);
    let cc = null, cb = null, ci = null;     // commands, doorbells, interrupts
    g.font = "9px ui-sans-serif, system-ui"; g.textBaseline = "middle";
    for (let k = sp.lo; k < sp.hi; k++) {
      const e = sp.items[k];
      if (e.ev === "fw_cmd") {
        const [a, b] = e.span;
        if (b < this.t0) continue;
        const x0 = this.x(a), x1 = Math.max(x0 + 2, this.x(b));
        // A command's class and whether it was placed exactly ride in the column's
        // state, so a run of them keeps its colour and its shade.
        if (sp.dense && x1 - x0 < WIDE) { this.mark(cc || (cc = this.cols(0)), x0, x1, 1 + 2 * HOST_CLASS.indexOf(hostClass(e.kind)) + (e.placed === 0 ? 1 : 0), true); continue; }
        g.fillStyle = this.hostColor(e.kind);
        if (e.placed === 0) g.fillRect(x0, y + 3, x1 - x0, h - 6);
        else { g.globalAlpha = 0.5; g.fillRect(x0, y + 3, x1 - x0, h - 6); g.globalAlpha = 1; g.setLineDash(e.placed === 1 ? [3, 2] : [1, 2]); g.strokeStyle = g.fillStyle; g.strokeRect(x0 + 0.5, y + 3.5, x1 - x0 - 1, h - 7); g.setLineDash([]); }
        if (x1 - x0 > 30) { g.fillStyle = col.u.bg; g.fillText(e.kind + (e.op ? ":" + e.op : ""), x0 + 2, y + h / 2); }
      } else if (e.ev === "doorbell") {
        if (e.ps < this.t0 || e.ps > this.t1) continue;
        const x = this.x(e.ps);
        // Pennants a pixel apart are a bar: keep the bar, not the thousands of paths.
        if (sp.dense) { this.mark(cb || (cb = this.cols(1)), x - 3, x + 3); continue; }
        g.fillStyle = col.u.host;
        g.beginPath(); g.moveTo(x - 3, y + h - 2); g.lineTo(x + 3, y + h - 2); g.lineTo(x, y + h - 7); g.closePath(); g.fill();
      } else if (e.ev === "irq") {
        if (e.ps < this.t0 || e.ps > this.t1) continue;
        const x = Math.round(this.x(e.ps)) + 0.5;
        if (sp.dense) { this.mark(ci || (ci = this.cols(2)), x - 0.5, x + 0.5); continue; }
        g.strokeStyle = col.u.host; g.beginPath(); g.moveTo(x, y + 1); g.lineTo(x, y + 6); g.stroke();
      }
    }
    if (cc) this.runs(cc, (a, b, s) => {
      g.fillStyle = this.hostColorOf(HOST_CLASS[(s - 1) >> 1]);
      g.globalAlpha = (s - 1) & 1 ? 1 : 0.5;    // an interpolated command keeps its paler shade
      g.fillRect(a, y + 3, b - a, h - 6);
      g.globalAlpha = 1;
    });
    g.fillStyle = col.u.host;
    if (cb) this.runs(cb, (a, b) => g.fillRect(a, y + h - 7, b - a, 5));
    if (ci) this.runs(ci, (a, b) => g.fillRect(a, y + 1, b - a, 5));
  }

  paintOverlay() {
    const g = this.og, W = this.W, H = this.H, dpr = this.dpr, col = this.colors();
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, H);
    const top = this.body.scrollTop;
    const s = this.store.get();
    // Row highlight.
    if (this.hl) {
      const r = this.rows.find((x) => x.id === this.hl);
      if (r) { g.fillStyle = col.u.accent; g.globalAlpha = 0.08; g.fillRect(0, r.y - top, W, r.h); g.globalAlpha = 1; }
    }
    // Selection and hover outlines.
    for (const [item, color, lw] of [[s.selection, col.u.sel, 2], [s.hover, col.u.fg, 1]]) {
      const box = item && this.boxOf(item);
      if (!box) continue;
      g.strokeStyle = color; g.lineWidth = lw;
      g.strokeRect(box.x0 - 1, box.y - top - 1, Math.max(2, box.x1 - box.x0) + 2, box.h + 2);
    }
    g.lineWidth = 1;
    // Cursor.
    if (!Number.isNaN(this.cursor)) {
      const x = Math.round(this.x(this.cursor)) + 0.5;
      g.strokeStyle = col.u.sel; g.beginPath(); g.moveTo(x, 0); g.lineTo(x, H); g.stroke();
    }
    // Arrows between rows.
    for (const a of this.arrows) this.paintArrow(g, a, top);
  }

  paintArrow(g, a, top) {
    const col = this.colors();
    const rf = this.rows.find((r) => r.id === a.fromRow), rt = this.rows.find((r) => r.id === a.toRow);
    if (!rt) return;
    const x1 = this.x(a.toT), y1 = rt.y - top + rt.h / 2;
    g.strokeStyle = a.color || col.u.sel; g.fillStyle = g.strokeStyle; g.lineWidth = 1.5;
    if (!rf) {
      g.font = "10px ui-sans-serif, system-ui"; g.textBaseline = "middle";
      g.fillText("← " + (a.fromLabel || a.fromRow), x1 + 6, y1);
      g.beginPath(); g.arc(x1, y1, 3, 0, Math.PI * 2); g.fill();
      return;
    }
    const x0 = this.x(a.fromT), y0 = rf.y - top + rf.h / 2;
    g.beginPath(); g.moveTo(x0, y0);
    const cx = (x0 + x1) / 2;
    g.bezierCurveTo(cx, y0, cx, y1, x1, y1);
    g.stroke();
    const ang = Math.atan2(y1 - y0, x1 - cx);
    g.beginPath(); g.moveTo(x1, y1);
    g.lineTo(x1 - 8 * Math.cos(ang - 0.4), y1 - 8 * Math.sin(ang - 0.4));
    g.lineTo(x1 - 8 * Math.cos(ang + 0.4), y1 - 8 * Math.sin(ang + 0.4));
    g.closePath(); g.fill();
    g.beginPath(); g.arc(x0, y0, 3, 0, Math.PI * 2); g.fill();
    g.lineWidth = 1;
  }

  // The on-screen box of a selectable item, if its row is shown.
  boxOf(item) {
    const m = this.model;
    const r = this.rows.find((x) => x.id === item.row);
    if (!r) return null;
    let a, b;
    if (item.kind === "bundle") { const c = item.core; a = c.t[item.bi]; b = a + c.cyclePs; }
    else if (item.kind === "wait") { a = item.wait.t0; b = item.wait.open ? m.tEnd : item.wait.t1; }
    else if (item.kind === "stall") { a = item.stall.t0; b = item.stall.open ? m.tEnd : item.stall.t1; }
    else if (item.kind === "dma") { a = Number.isNaN(item.dma.at) ? item.dma.start : item.dma.at; b = item.dma.open ? a : item.dma.end; }
    else if (item.kind === "host") { a = item.host.span ? item.host.span[0] : item.host.ps; b = item.host.span ? item.host.span[1] : item.host.ps; }
    else if (item.kind === "flag") { a = item.flag.at; b = a; }
    else if (item.kind === "scdispatch") { a = item.sc.ps; b = a; }
    else if (item.kind === "nop") { a = item.start; b = item.end; }
    else if (item.kind === "unit") { const c = item.core; a = c.t[item.bi]; b = a + c.cyclePs; }
    else if (item.kind === "bin") { a = item.t0; b = item.t1; }
    else return null;
    return { x0: this.x(a), x1: this.x(b), y: r.y + 1, h: r.h - 2 };
  }

  // --- hit testing -----------------------------------------------------------------

  hitTest(x, y) {
    const r = this.rowAtY(y);
    if (!r) return null;
    const t = this.t(x), m = this.model, tol = 3 * this.psPerPx;
    const rowKey = r.core ? r.core.key : r.id;
    // Pointing at a dense row must not walk it: the last event that started at or
    // before the point is a binary search away, and the running maximum of the ends
    // (rowSpan) says when walking back can stop -- no earlier event reaches the point.
    // Where several cover the point, `earliest` picks the one the scans these replaced
    // picked: the first in start order for stalls and DMAs, the last for waits.
    const backFrom = (sp, hit, earliest) => {
      let got = null;
      for (let k = lowerIndex(sp.starts, t + tol); k >= 0 && sp.ends[k] >= t - tol; k--) {
        const one = hit(sp.items[k]);
        if (one) { if (!earliest) return one; got = one; }
      }
      return got;
    };
    const waitHit = () => {
      const ids = this.links.waitsByRow.get(rowKey);
      if (!ids || !ids.length) return null;
      const sp = this.rowSpan("w" + rowKey, ids, (i) => m.waits[i].t0, (i) => (m.waits[i].open ? m.tEnd : m.waits[i].t1));
      return backFrom(sp, (i) => {
        const w = m.waits[i];
        return t <= (w.open ? m.tEnd : w.t1) + tol ? { kind: "wait", row: r.id, wait: w } : null;
      });
    };
    const dmaHit = (key) => {
      const ids = this.links.dmaByIssuer.get(key);
      if (!ids || !ids.length) return null;
      const sp = this.rowSpan("d" + key, ids, (i) => dmaT0(m.dmas[i]), (i) => dmaT1(m.dmas[i]));
      // An open DMA is only where it was issued; a landed one is its whole bar.
      return backFrom(sp, (i) => {
        const d = m.dmas[i];
        return t <= (d.open ? dmaT0(d) : d.end) + tol ? { kind: "dma", row: r.id, dma: d } : null;
      }, true);
    };
    const flagHit = (key) => {
      const ids = this.links.flagsByRow.get(key);
      if (!ids || !ids.length) return null;
      const sp = this.rowSpan("f" + key, ids, (i) => m.flags[i].at, (i) => m.flags[i].at);
      // The nearest write within the tolerance: writes are points on a sorted axis, so
      // the one before the pointer and the one after it are the only candidates.
      let best = null;
      let k0 = lowerIndex(sp.starts, t);
      while (k0 > 0 && sp.starts[k0 - 1] === sp.starts[k0]) k0--;
      for (let k = Math.max(0, k0); k <= k0 + 1 && k < sp.items.length; k++) {
        const f = m.flags[sp.items[k]], d = Math.abs(f.at - t);
        if (d <= 2 * tol && (!best || d < best.d)) best = { d, f };
      }
      return best ? { kind: "flag", row: r.id, flag: best.f } : null;
    };
    const stallHit = () => {
      const ids = this.links.stallsByRow.get(rowKey);
      if (!ids || !ids.length) return null;
      const sp = this.rowSpan("s" + rowKey, ids, (i) => m.stalls[i].t0, (i) => (m.stalls[i].open ? m.tEnd : m.stalls[i].t1));
      return backFrom(sp, (i) => {
        const s = m.stalls[i];
        return t <= (s.open ? m.tEnd : s.t1) + tol ? { kind: "stall", row: r.id, stall: s } : null;
      }, true);
    };
    const scHit = (chip, ci) => {
      for (const d of m.scDispatchesOf(chip, ci)) if (Math.abs(d.ps - t) <= 2 * tol) return { kind: "scdispatch", row: r.id, sc: d };
      return null;
    };
    if (r.kind === "tc") {
      const c = r.core;
      const w = waitHit() || stallHit();
      if (w) return w;
      if (c.kind === "sc") {
        const sc = scHit(c.chip, c.ci);
        if (sc) return sc;
      }
      if (this.psPerPx < 2 * c.cyclePs) {
        const bi = m.bundleAt(c, t);
        if (bi >= 0 && t < c.t[bi] + c.cyclePs) return { kind: "bundle", row: r.id, core: c, bi };
        // In an empty-bundle run?
        const next = bi + 1;
        if (next < c.n && bi >= 0) {
          const nop = m.nopAtStep(c, c.step[bi] + 1);
          if (nop) return { kind: "nop", row: r.id, core: c, nop, start: c.t[next] - nop.n * c.cyclePs, end: c.t[next] };
        }
        return null;
      }
      const bs = this.bins.core(c), level = levelFor(bs, this.psPerPx);
      const out = sampleColumn(level, t - this.psPerPx / 2, t + this.psPerPx / 2, {});
      return { kind: "bin", row: r.id, core: c, t0: t - this.psPerPx / 2, t1: t + this.psPerPx / 2, bin: { ...out } };
    }
    if (r.kind === "chip") {
      const level = levelFor(this.bins.chip(r.chip), this.psPerPx);
      const out = sampleColumn(level, t - this.psPerPx / 2, t + this.psPerPx / 2, {});
      return { kind: "bin", row: r.id, chip: r.chip, t0: t - this.psPerPx / 2, t1: t + this.psPerPx / 2, bin: { ...out } };
    }
    if (r.kind === "tc.dma") return dmaHit(r.core.key);
    if (r.kind === "tc.units") {
      const c = r.core, bi = m.bundleAt(c, t);
      if (bi < 0 || t >= c.t[bi] + 2 * c.cyclePs) return null;
      const us = m.unitsAtBundle(c, bi);
      return us.length ? { kind: "unit", row: r.id, core: c, bi, units: us } : null;
    }
    if (r.kind === "sc") return scHit(r.chip, r.ci) || waitHit() || flagHit(r.id) || dmaHit(r.id);
    if (r.kind === "host") {
      const sp = this.hostSpan(r.chip);
      // The event whose nearer edge is closest to the pointer, among those under it.
      let best = null;
      for (let k = lowerIndex(sp.starts, t + tol); k >= 0 && sp.ends[k] >= t - tol; k--) {
        const e = sp.items[k], a = hostT0(e), b = hostT1(e);
        if (t > b + tol) continue;
        const d = Math.min(Math.abs(t - a), Math.abs(t - b));
        if (!best || d <= best.d) best = { d, e };   // walking back, ties keep the earliest
      }
      return best ? { kind: "host", row: r.id, host: best.e } : null;
    }
    return null;
  }

  tipFor(hit) {
    const m = this.model;
    if (!hit) return null;
    if (hit.kind === "bundle") {
      const c = hit.core, i = hit.bi;
      const got = this.ctx.disasm ? this.ctx.disasm.get(c, i) : { slots: m.slotsOf(c, i) };
      const slots = got ? got.slots.map((s) => `<b>${esc(s.u)}</b> ${esc(slotText(c.kind, s.u, s.i))}`).join("  ·  ") : "rendering…";
      return `<b>${esc(c.label)}</b>  pc ${c.pc[i]}  ·  ${fmtPs(c.t[i])}\n${slots || "(empty bundle)"}`;
    }
    if (hit.kind === "nop") return `${hit.nop.n} empty bundles (compiler padding) from pc ${hit.nop.pc}`;
    if (hit.kind === "stall") {
      const s = hit.stall;
      return `<b>${esc(s.reason)} stall</b> on ${esc(s.core.label)} at pc ${esc(s.pc)}\nfrom ${fmtPs(s.t0)}${s.open ? ", never released" : " to " + fmtPs(s.t1)}\nstall ${fmtPs(s.stallPs)}${s.stall != null ? " (" + esc(s.stall) + " cycles)" : ""}${s.round != null ? "\nbarrier round " + esc(s.round) + ", mask 0x" + esc((s.mask || 0).toString(16)) : ""}`;
    }
    if (hit.kind === "wait") {
      const w = hit.wait, r = this.links.releaserOf(w);
      return `<b>${w.cls === "host" ? "idle for the host" : "waiting"}, flag ${esc(w.idx)}</b> · ${w.open ? "open" : fmtPs(w.stallPs)} from ${fmtPs(w.t0)}\nreleased by ${esc(r.text)}`;
    }
    if (hit.kind === "dma") {
      const d = hit.dma;
      return `<b>DMA #${esc(d.id)}</b> ${esc(dmaShort(d))} by ${esc(issuer(d.by))}${d.cross ? " (cross-chip)" : ""}\nissued ${fmtPs(d.at)}, ${fmtPs(d.dur)} on the pipe, landed ${fmtPs(d.end)}`;
    }
    if (hit.kind === "host") {
      const e = hit.host;
      return `<b>host</b> ${esc(hostLine(e))}\n${fmtPs(e.ps)}${e.placed === 0 ? "" : " (approximate)"}${e.done ? " · " + esc(e.done.status) : ""}`;
    }
    if (hit.kind === "flag") {
      const f = hit.flag;
      return `<b>${esc(f.op)} flag ${esc(f.idx)}</b> of chip ${esc(f.chip)} ${esc(coreShort(f.core, f.ci, f.tile))}  at ${fmtPs(f.at)}\n${esc(f.old)} → ${esc(f.new)}${f.delta != null ? " (" + (f.delta >= 0 ? "+" : "") + esc(f.delta) + ")" : ""}${f.cmd != null ? "  by the host, command #" + esc(f.cmd) : ""}`;
    }
    if (hit.kind === "scdispatch") {
      const d = hit.sc;
      return `<b>SparseCore ${esc(d.ci)} dispatch</b> on chip ${esc(d.chip)}  (control_tile, ${popcount(d.tileMask)} tiles, flags ${esc(d.flags)})\n${fmtPs(d.ps)} ${d.placed === 0 ? "" : "(placed by interpolation)"}`;
    }
    if (hit.kind === "unit") {
      return `<b>${esc(hit.core.label)}</b> step ${hit.core.step[hit.bi]}\n` + hit.units.map((u) => `${esc(u.unit)}${esc(u.idx)} ${esc(u.op)}`).join("\n");
    }
    if (hit.kind === "bin") {
      const b = hit.bin;
      const dom = b.dom === DOM_NONE ? "idle" : DOMINANT[b.dom];
      return `<b>${esc(hit.core ? hit.core.label : "chip " + hit.chip)}</b> at ${fmtPs(hit.t0)}\nissuing ${(b.busy * 100).toFixed(0)}% (${dom}) · waiting ${(b.wait * 100).toFixed(0)}% · idle for host ${(b.hwait * 100).toFixed(0)}% · padding ${(b.nop * 100).toFixed(0)}%`;
    }
    return null;
  }

  // --- interaction -----------------------------------------------------------------

  bind() {
    const cv = this.canvases;
    let drag = null;
    // A range being selected: on the axis, or with shift held on the rows. Releasing
    // zooms to it, as a profiler's overview does.
    const sel = el("div", "tl-select");
    sel.hidden = true;
    this.root.appendChild(sel);
    const showSel = (xa, xb) => { sel.hidden = false; sel.style.left = (GUTTER + Math.min(xa, xb)) + "px"; sel.style.width = Math.max(1, Math.abs(xb - xa)) + "px"; };
    const endSel = (xa, xb) => {
      sel.hidden = true;
      if (Math.abs(xb - xa) <= 3) return false;
      this.setView(this.t(Math.min(xa, xb)), this.t(Math.max(xa, xb)));
      return true;
    };
    const axisX = (e) => e.clientX - this.axis.getBoundingClientRect().left - GUTTER;
    let asel = null;
    this.axis.addEventListener("pointerdown", (e) => {
      if (e.button !== 0 || axisX(e) < 0) return;
      asel = { x0: axisX(e) };
      try { this.axis.setPointerCapture(e.pointerId); } catch (_) { /* synthetic events */ }
      e.preventDefault();
    });
    this.axis.addEventListener("pointermove", (e) => { if (asel) showSel(asel.x0, axisX(e)); });
    this.axis.addEventListener("pointerup", (e) => { if (!asel) return; const a = asel; asel = null; endSel(a.x0, axisX(e)); });
    this.axis.addEventListener("pointercancel", () => { asel = null; sel.hidden = true; });
    cv.addEventListener("wheel", (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        this.zoomAt(e.offsetX, Math.exp(e.deltaY * 0.002));
      } else if (e.shiftKey || (Math.abs(e.deltaX) > Math.abs(e.deltaY))) {
        e.preventDefault();
        this.panPx(e.deltaX || e.deltaY);
      }
    }, { passive: false });
    // The cursor line can be taken and dragged to sweep through time; whoever
    // follows the cursor (the chip page's listing) hears about it through onCursor.
    const onCursorLine = (x) => !Number.isNaN(this.cursor) && Math.abs(x - this.x(this.cursor)) <= 4;
    cv.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      drag = { x: e.clientX, y: e.clientY, ox: e.offsetX, t0: this.t0, t1: this.t1, moved: false, select: e.shiftKey, sweep: !e.shiftKey && onCursorLine(e.offsetX) };
      try { cv.setPointerCapture(e.pointerId); } catch (_) { /* synthetic events */ }
    });
    cv.addEventListener("pointermove", (e) => {
      if (drag) {
        const dx = e.clientX - drag.x;
        if (Math.abs(dx) > 3) drag.moved = true;
        if (!drag.moved) return;
        if (drag.sweep) {
          hideTip();
          cv.style.cursor = "ew-resize";
          const t = this.t(e.offsetX);
          this.setCursor(t);
          if (this.opts.onCursor) this.opts.onCursor(t);
          return;
        }
        if (drag.select) { hideTip(); showSel(drag.ox, drag.ox + dx); cv.style.cursor = "crosshair"; return; }
        const d = -dx * this.psPerPx;
        this.setView(drag.t0 + d, drag.t1 + d);
        cv.style.cursor = "grabbing";
        return;
      }
      const hit = this.hitTest(e.offsetX, e.offsetY);
      const prev = this.store.get().hover;
      if (!sameHit(prev, hit)) {
        this.store.set({ hover: hit });
        this.invalidate(2);
      }
      const tip = this.tipFor(hit);
      if (tip) showTip(e.clientX, e.clientY, tip); else hideTip();
      cv.style.cursor = onCursorLine(e.offsetX) ? "ew-resize" : hit && hit.kind !== "bin" ? "pointer" : "default";
    });
    cv.addEventListener("pointerleave", () => { hideTip(); if (this.store.get().hover) { this.store.set({ hover: null }); this.invalidate(2); } });
    cv.addEventListener("pointerup", (e) => {
      if (!drag) return;
      const d = drag;
      drag = null;
      cv.style.cursor = "default";
      if (d.select && d.moved) { endSel(d.ox, d.ox + e.clientX - d.x); return; }
      if (d.moved) return;
      const hit = this.hitTest(e.offsetX, e.offsetY);
      if (hit && hit.kind === "bin") {
        // Zoomed out, a click lands on a density bin rather than a box; the bundle
        // under it is still one bundle, the one issued at or before that time, and
        // that is what gets selected and shown.
        const t = this.t(e.offsetX), c = hit.core;
        const bi = c && c.n ? Math.max(0, this.model.bundleAt(c, t)) : -1;
        const sel = bi >= 0 ? { kind: "bundle", row: hit.row, core: c, bi } : null;
        this.setCursor(t);
        this.store.set({ selection: sel });
        if (this.opts.onSelect) this.opts.onSelect(sel, t);
        this.invalidate(2);
        return;
      }
      this.setCursor(this.t(e.offsetX));
      this.store.set({ selection: hit });
      if (this.opts.onSelect) this.opts.onSelect(hit, this.t(e.offsetX));
      this.invalidate(2);
    });
    cv.addEventListener("dblclick", (e) => {
      e.preventDefault();
      const hit = this.opts.onItemDblClick ? this.hitTest(e.offsetX, e.offsetY) : null;
      if (hit && hit.kind !== "bin" && this.opts.onItemDblClick(hit, this.t(e.offsetX))) return;
      this.zoomAt(e.offsetX, 0.5);
    });
    this.axis.addEventListener("wheel", (e) => { e.preventDefault(); this.zoomAt(e.offsetX - GUTTER, Math.exp(e.deltaY * 0.002)); }, { passive: false });
  }
}

function sameHit(a, b) {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind || a.row !== b.row) return false;
  if (a.kind === "bundle" || a.kind === "unit") return a.bi === b.bi;
  if (a.kind === "wait") return a.wait === b.wait;
  if (a.kind === "stall") return a.stall === b.stall;
  if (a.kind === "dma") return a.dma === b.dma;
  if (a.kind === "host") return a.host === b.host;
  if (a.kind === "flag") return a.flag === b.flag;
  if (a.kind === "scdispatch") return a.sc === b.sc;
  if (a.kind === "nop") return a.nop.i === b.nop.i;
  if (a.kind === "bin") return Math.abs(a.t0 - b.t0) < 1e-9;
  return false;
}

export function popcount(n) {
  let c = 0;
  for (let v = n >>> 0; v; v >>>= 1) c += v & 1;
  return c;
}
